import {
    ChangeDetectionStrategy,
    Component,
    Inject,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { EnvironmentService } from '@core/services/environment.service';
import { EventTrackerService } from '@core/services/event-tracker.service';
import { LOCALSTORAGE } from '@core/services/local-storage.service';
import { WINDOW } from '@core/services/window.service';
import { ANALYTICS_EVENTS } from '@core/utils/AnalyticsEvents';
import { LeadMagnetActiveCampaignModalComponent } from '@features/lead-magnet/components/lead-magnet-active-campaign-modal/lead-magnet-active-campaign-modal.component';
import { ModalService } from '@features/modal/services/modal.service';
import { ActiveCampaignLeadMagnet } from '@generated/graphql';
import { Subject, fromEvent } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lead-magnet-exit-intent',
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class LeadMagnetExitIntentComponent implements OnInit, OnDestroy {
    @Input() leadMagnet: ActiveCampaignLeadMagnet;
    private destroy$ = new Subject();

    constructor(
        private environmentService: EnvironmentService,
        private modalService: ModalService,
        private eventTrackerService: EventTrackerService,
        @Inject(WINDOW) private window: Window,
        @Inject(LOCALSTORAGE) private localStorage: Storage
    ) {}

    private get localStorageKey() {
        return `exitIntentLastDisplayed_${this.leadMagnet?.sys.id}`;
    }

    ngOnInit(): void {
        if (this.environmentService.isBrowser()) {
            const mouseOutEvent = fromEvent<MouseEvent>(
                this.window,
                'mouseout'
            );
            const exitIntentLastDisplayed =
                +this.localStorage.getItem(this.localStorageKey) +
                30 * 24 * 60 * 60 * 1000;
            mouseOutEvent
                .pipe(
                    filter(() => !this.modalService.active.value),
                    filter(data => data.clientY <= 0),
                    filter(() => exitIntentLastDisplayed < Date.now()),
                    map(() => this.leadMagnet),
                    filter(data => !!data),
                    take(1),
                    takeUntil(this.destroy$)
                )
                .subscribe(() => {
                    this.showModal();
                    this.eventTrackerService.trackEvent(
                        ANALYTICS_EVENTS.LEAD_MAGNET_EXIT_INTENT,
                        {
                            leadMagnet: this.leadMagnet.title,
                            tag: this.leadMagnet.activeCampaignTag,
                        }
                    );
                });
        }
    }

    showModal() {
        this.modalService.show(LeadMagnetActiveCampaignModalComponent, {
            leadMagnet: this.leadMagnet,
            isExitIntent: true,
        });
        this.localStorage.setItem(this.localStorageKey, `${Date.now()}`);
    }

    ngOnDestroy(): void {
        this.destroy$.next(null);
        this.destroy$.complete();
    }
}
